import React, { useRef, useEffect } from 'react';
import { useTextLanguage } from '../../../../hooks/useShared';
import { useSelector, useDispatch } from 'react-redux';
import { trimLowerCase } from '../../../../helpers/shared';
import styled from 'styled-components';
import { setIBV } from '../../../../redux/actions/base';
import { useTranslation } from 'react-i18next';
import closeIcon from '../../../../assets/images/token-modal/close.png';
import { IoCloseOutline } from "react-icons/io5";
import Modal from 'react-modal';
import { CloseImage } from '../../styled';
import { Row, Col, Button } from 'react-bootstrap';

/**
 * Component for the Opt In modal of confirmation, in the final tier, before migration
 * @returns
 */
export const OptInModal = ({
  onChangeStep,
  modalIsOpen,
  setModal,
  handleOpenIbv,
  appearance = 'default'
}) => {
  const { t } = useTranslation()
  const { languageText } = useTextLanguage()
  const currentLanguage = languageText?.split('_')[1]
  const { optInModalInfo } = useSelector(store => store.base)
  let info = optInModalInfo?.filter(infoSet => infoSet.language_code === trimLowerCase(currentLanguage))[0]
  let conditions = info ? Object?.keys(info)?.filter(key => key?.includes('text_'))
    .map(text => info[text]) : null
  const [acceptedConditions, setAcceptedConditions] = React.useState({})
  const { isIBVVerified, requestWidgetURL } = useSelector(state => state.base.ibv) || {}
  const dispatch = useDispatch()

  React.useEffect(() => {
    setTimeout(() => {
      if (conditions && Object.keys(acceptedConditions).length === 0) {
        let initialAcceptedConditions = {};
        conditions.forEach((_, idx) => {
          initialAcceptedConditions[`condition_${idx + 1}`] = false;
        });
        setAcceptedConditions(initialAcceptedConditions);
      }
    }, 1100);
  }, [conditions, acceptedConditions]);

  React.useEffect(() => {
    setTimeout(() => {
      if (!modalIsOpen) {
        let initialAcceptedConditions = {};
        conditions.forEach((_, idx) => {
          initialAcceptedConditions[`condition_${idx + 1}`] = false;
        });
        setAcceptedConditions(initialAcceptedConditions);
      }
    }, 1100);

  }, [modalIsOpen]);

  
  const [enabled, setEnabled] = React.useState(false)
  const acceptedConditionsValues = Object.values(acceptedConditions)
  React.useEffect(() => {
    const isEnabled = Object.values(acceptedConditions)?.length && Object.values(acceptedConditions).every(condition => condition);
    setEnabled(isEnabled);
  }, [acceptedConditions, acceptedConditionsValues]);

  const handleClose = () => {
    setAcceptedConditions(prevConditions => {
      let resettedConditions = {}
      Object.keys(prevConditions).forEach(condition => {
        resettedConditions[condition] = false
      })
      return resettedConditions
    })
    setModal(false)
    const params = {
      pendingVerification: false
    }
    dispatch(setIBV(params))
  }

  /* Should send form or trigger IBV? */
  const shouldValidate = (requestWidgetURL?.length && !isIBVVerified)

  return (
    <>
      { (appearance === 'default') && (
        <Modal
          ariaHideApp={ false }
          isOpen={modalIsOpen}
          onAfterClose={ handleClose }
        >
          <CloseImage
            alt=""
            onClick={handleClose}
            src={ closeIcon }
            name="image_close"
          />

          {
            conditions ? conditions.map((condition, idx) => {
              return (
                <Condition 
                  condition={condition}
                  value={acceptedConditions[`condition_${idx + 1}`]}
                  idx={idx}
                  numberOfCondition={conditions?.length + 1}
                  setAcceptedConditions={setAcceptedConditions}
                  /*'key' is just for avoiding error in console. Remember using indexex as 'key' is a bad idea when trying any further logic with this array's elements */
                  key={idx} 
                />
              )
            }) : null
          }
          {
            conditions ? (
              <>
                <p className="mt-2" dangerouslySetInnerHTML={{__html: info?.additional_text}} />  
                {/* {product && children } */}
                <Row className="justify-content-center m-0">
                  <Col className="p-2 mt-2 mb-2" xs={12} md={6}>
                    <Button
                      className="btn mb-0"
                      type="submit"
                      color="first"
                      disabled={!enabled}
                      onClick={ shouldValidate ? handleOpenIbv : onChangeStep }
                      name="btn_modal"
                    >
                      {t("common.accept")}
                    </Button>
                  </Col>
                  <Col className="p-2 mt-2 mb-2" xs={12} md={6}>
                    <Button
                      className="btn mb-0"
                      type="button"
                      color="second"
                      onClick={ handleClose }
                      name="btn_modal_cancel"
                    >
                      {t("common.cancel")}
                    </Button>
                  </Col>
                </Row>
              </>
            ) : null
          }
          {/* Warning message if opt-in info could be fetcched */}
          {
            !conditions ? (
              <div className="alert alert-info mt-4" role="alert">
                { t("modalOptIn.termsNotFound_1") } <br />
                { t("modalOptIn.termsNotFound_2") }
              </div>
            ) : null
          }
    
        </Modal>
      ) }
      {
        (appearance === 'new') && (
          <OptInModalBS
            t={t}
            modalIsOpen={modalIsOpen}
            handleClose={handleClose}
            conditions={conditions}
            acceptedConditions={acceptedConditions}
            info={info}
            enabled={enabled}
            shouldValidate={shouldValidate}
            handleOpenIbv={handleOpenIbv}
            onChangeStep={onChangeStep}
            setAcceptedConditions={setAcceptedConditions}
          />
        )
      }
    </>
  );
}

const Condition = ({
  condition,
  idx,
  numberOfCondition,
  setAcceptedConditions,
  value
}) => {
  const last = (Number(idx) + 1) === Number(numberOfCondition)

  const handleCondition = (e) => {
    setAcceptedConditions(prevConditions => {
      return {
        ...prevConditions,
        [`condition_${Number(idx) + 1}`]: e.target.checked
      }
    })
  }

  return (
    <ConditionDiv>
      <input 
        type='checkbox' 
        name={`condition_${idx}`} 
        onChange={handleCondition} 
        checked={value ?? false}
      />
      <label
        className={ last ? 'last' : '' }
        htmlFor={`condition_${idx}`}
        dangerouslySetInnerHTML={{ __html: condition }}
      />
    </ConditionDiv>
  )
}


const OptInModalBS = ({
  modalIsOpen,
  handleClose,
  conditions,
  acceptedConditions,
  setAcceptedConditions,
  info,
  enabled,
  shouldValidate,
  handleOpenIbv,
  onChangeStep,
  t
}) => {
  const modalRef = useRef(null);
  const bsModalRef = useRef(null);

  // Modal init
  useEffect(() => {
      if (!modalRef.current) return;

      let isMounted = true;

      const initializeModal = async () => {
        try {
          const { Modal } = await import('bootstrap');
          
          if (!isMounted) return;

          bsModalRef.current = new Modal(modalRef.current, {
            backdrop: 'static',
            keyboard: true,
            focus: true
          });
        } catch (error) {
          console.error("Error al cargar Bootstrap Modal:", error);
        }
      };

      // Delay making sure the DOM is ready
      const timer = setTimeout(initializeModal, 50);

      return () => {
        isMounted = false;
        clearTimeout(timer);
        if (bsModalRef.current) {
          bsModalRef.current.dispose();
          bsModalRef.current = null;
        }
      };
  }, []);

  useEffect(() => {
      if (!bsModalRef.current) return;

      if (modalIsOpen) {
          bsModalRef.current.show();
      } else {
          bsModalRef.current.hide();
      }
  }, [modalIsOpen]);

  return (
    <div 
      ref={modalRef}
      className="modal fade" 
      tabIndex="-1" 
      aria-labelledby="optInModalLabel" 
    >
      <div className="modal-dialog modal-dialog-centered">
        <ModalContent className="modal-content glass-modal">  
          <ModalHeader className="modal-header border-0 pb-1">
            <Title as="h5" id="optInModalLabel">
              {t('modalOptIn.terms')}
            </Title>
            <CloseButton 
              onClick={handleClose}
              aria-label="Close"
            />
          </ModalHeader>

          {/* Body */}
          <div className="modal-body">
            {conditions ? conditions.map((condition, idx) => (
              <Condition
                key={idx}
                condition={condition}
                value={acceptedConditions[`condition_${idx + 1}`]}
                idx={idx}
                numberOfCondition={conditions?.length + 1}
                setAcceptedConditions={setAcceptedConditions}
              />
            )) : null}

            {conditions && (
              <LegalText 
                dangerouslySetInnerHTML={{ __html: info?.additional_text }} 
              />
            )}

            {!conditions && (
              <div className="alert alert-info mt-4" role="alert">
                {t("modalOptIn.termsNotFound_1")} <br />
                {t("modalOptIn.termsNotFound_2")}
              </div>
            )}
          </div>

          {/* Footer */}
          {conditions && (
          <div className="modal-footer border-0 pt-2">
            <ButtonsContainer>
              <AcceptButton
                type="button"
                disabled={!enabled}
                onClick={shouldValidate ? handleOpenIbv : onChangeStep}
              >
                {t("common.accept")}
              </AcceptButton>

              <CancelButton
                type="button"
                onClick={handleClose}
              >
                {t("common.cancel")}
              </CancelButton>
            </ButtonsContainer>
          </div>
          )}
        </ModalContent>
      </div>
    </div>
  );
};

// ? Styled components
const ConditionDiv = styled.div`
  margin-top:.55rem;
  display:flex;
  gap: .55rem;
  align-items: baseline;
  & > input {
    position: relative;
    top: .55rem;
  }
  .last p {
    margin-bottom: 0px;
  }
`

const ModalContent = styled.div`
    background: hsla(248.57142857142858, 41.1764705882353%, 23.666666666666664%, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(140, 100, 255, 0.25);
    border-radius: 20px;
    padding: 22px 24px 28px;
    color: #E0E7FF;
    max-width: 480px;
    width: 100%;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CloseButton = styled(IoCloseOutline)`
    cursor: pointer;
    color: white;
    font-size: 36px;
`;

const Title = styled.h2`
    font-size: 1.4rem;
    font-weight: 600;
    text-align: center;
    color: #FFFFFF;
`;

const LegalText = styled.p`
    font-size: 0.86rem;
    line-height: 1.55;
    color: whitesmoke;
    margin: 24px 0;
`;

const ButtonsContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 8px;
`;

const AcceptButton = styled.button`
    flex: 1;
    padding: 14px 20px;
    background: linear-gradient(90deg, #6366F1, #8B5CF6);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const CancelButton = styled.button`
    flex: 1;
    padding: 14px 20px;
    background: #334155;
    color: #CBD5E1;
    border: 1px solid #475569;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #475569;
    }
`;